<template>
  <form
    class="w-full flex"
    :class="classForm || brandClasses.form"
    role="search"
    @submit.prevent="submit"
  >
    <define-submit-button>
      <vf-loader v-if="loading" class="my-a ml-3 h-6 w-6" />
      <base-button
        v-else
        :aria-label="$t.searchSubmitLabel"
        :class="classSubmit || brandClasses.submit"
        type="submit"
      >
        <vf-icon :name="iconNameSubmit" :size="iconSizeSubmit" />
      </base-button>
    </define-submit-button>

    <submit-button v-if="isSubmitPositionStart" />

    <vf-input class="grow |bg-transparent" :class="classInput || brandClasses.input">
      <span :id="id" class="sr-only">
        {{ label || $t.searchLabel }}
      </span>
      <template #input>
        <base-input
          ref="inputRef"
          v-model.trim="modelValue"
          :aria-labelledby="id"
          class="h-full focus-visible:outline-none"
          :maxlength
          :placeholder="placeholder || $t.searchPlaceholder"
          type="text"
          @keyup.tab="$emit('focus', $event)"
        />
      </template>
      <template #end>
        <base-button
          v-if="modelValue"
          :aria-label="$t.searchClearLabel"
          :class="brandClasses.clear"
          @click="clear"
        >
          <vf-icon :name="iconNameClear" :size="iconSizeClear" />
        </base-button>
      </template>
    </vf-input>

    <submit-button v-if="!isSubmitPositionStart" />
  </form>
</template>

<script lang="ts" setup>
import type { BaseInput as BaseInputType } from '#components'

const { submitPosition } = defineProps<{
  /**
   * Defines visually hidden label for input
   */
  label?: string
  /**
   * Indicates loading state
   */
  loading?: boolean
  /**
   * Defines max length for input
   */
  maxlength?: number
  /**
   * Defines placeholder when input is empty
   */
  placeholder?: string
  /**
   * Defines whether submit button appears before or after input
   */
  submitPosition?: 'start' | 'end'
  /**
   * Classes to be applied to form element
   */
  classForm?: string
  /**
   * Classes to be applied to input element
   */
  classInput?: string
  /**
   * Classes to be applied to submit button
   */
  classSubmit?: string
}>()

const emit = defineEmits<{
  change: [value: string]
  clear: []
  focus: [value?: string]
  submit: [value: string]
}>()

const {
  brandClasses,
  defaultSubmitPosition = 'end',
  iconNameClear,
  iconNameSubmit,
  iconSizeClear,
  iconSizeSubmit
} = useAppConfig().components.vf.searchBar
const id = useId()

const modelValue = defineModel<string>()

const [DefineSubmitButton, SubmitButton] = createReusableTemplate()

const inputRef = ref<InstanceType<typeof BaseInputType>>()

const isSubmitPositionStart = computed(() => (submitPosition || defaultSubmitPosition) === 'start')

const clear = () => {
  emit('clear')
  modelValue.value = ''
  inputRef.value?.$el.focus()
}

const submit = () => {
  if (modelValue.value) {
    emit('submit', modelValue.value)
  }
}

defineExpose({
  inputRef
})
</script>
